import { Edit, Trash2, User } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { useMemo } from 'react'
import { roles } from '../constants/roles'
import type { Church } from '@/features/churches/types/church'
import type { User as UserType } from '@/features/users/types/user'
import { UserRoleDisplayName, UserRoleEnum } from '@/features/users/types/user'
import { Badge } from '@/components/custom/badge'
import { Button } from '@/components/ui/button'
import LoadingButton from '@/components/custom/loading-button'

interface UserCardProps {
  user: UserType
  churches: Array<Church>
  isCurrentUser: boolean
  onEdit: (userId: string) => void
  onDelete: (userId: string) => void
  isSending: boolean
  sendSetupEmail: (email: string) => void
}

export function UserCard({
  user,
  churches,
  isCurrentUser,
  onEdit,
  onDelete,
  isSending,
  sendSetupEmail,
}: UserCardProps) {
  const navigate = useNavigate()

  const userChurches = useMemo(
    () => churches.filter((c) => user.churchId?.includes(c._id)),
    [churches, user.churchId],
  )
  const userRoles = useMemo(
    () => roles.filter((r) => user.roles.includes(r._id)),
    [user.roles],
  )
  const userRoleDisplayNames = useMemo(
    () =>
      userRoles
        .map((r) => {
          const roleName =
            UserRoleDisplayName[r._id as keyof typeof UserRoleDisplayName]

          return r._id === UserRoleEnum.PASTOR && user.isAdminPastor === true
            ? `${roleName} (Admin)`
            : roleName
        })
        .sort()
        .join(', '),
    [user.isAdminPastor, userRoles],
  )

  return (
    <div className="border rounded-lg p-4 hover:shadow-md transition-shadow bg-white">
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold text-lg">{user.name}</div>
        <Badge color={user.active ? 'green' : 'gray'}>
          {user.active ? 'Active' : 'Inactive'}
        </Badge>
      </div>

      <div className="space-y-2 mb-4">
        <div className="text-sm">
          <span className="font-medium text-gray-600">Email: </span>
          <span>{user.email}</span>
        </div>
        <div className="text-sm">
          <span className="font-medium text-gray-600">Roles: </span>
          <span>{userRoleDisplayNames}</span>
        </div>
        <div className="text-sm">
          <span className="font-medium text-gray-600">Churches: </span>
          <span>
            {userChurches.map((c, index) => (
              <span key={c._id}>
                {c.name}
                {index < userChurches.length - 1 ? ', ' : ''}
              </span>
            ))}
          </span>
        </div>
      </div>

      {/* Action Buttons */}
      {!isCurrentUser ? (
        <div className="flex flex-wrap gap-2">
          {!user.isVerified && (
            <LoadingButton
              isLoading={isSending}
              variant="outline"
              size="sm"
              className="w-full sm:w-auto"
              onClick={() => sendSetupEmail(user.email)}
            >
              Send Setup Email
            </LoadingButton>
          )}

          <Button
            variant="outline"
            size="sm"
            className="w-full sm:w-auto"
            onClick={() => onEdit(user._id)}
          >
            <Edit className="h-4 w-4 sm:mr-2" /> Edit
          </Button>

          <Button
            variant="destructive"
            size="sm"
            className="w-full sm:w-auto"
            onClick={() => onDelete(user._id)}
          >
            <Trash2 className="h-4 w-4 sm:mr-2" /> Delete
          </Button>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => {
            navigate({ to: '/profile' })
          }}
        >
          <User className="h-4 w-4 mr-2" />
          View User Profile
        </Button>
      )}
    </div>
  )
}
